import { useRef, useState, type MouseEvent } from 'react';
import { Link, navigate } from '../../app/router';
import { LeaveBoard } from './LeaveBoard';
import { useLayout } from '../../app/layout';
import { MenuSheet } from './MenuSheet';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { IconButton, Logo, Menu } from '../../ui';
import { Icon } from '../../ui/icons';
import { Kbd } from '../../ui/Kbd';
import { formatCombo } from './ShortcutsHelp';
import type { MainMenuEntry } from './main-menu';

const trigger =
  'inline-flex h-9 w-6 shrink-0 items-center justify-center rounded-full text-fg-muted pointer-coarse:w-11 os-press hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus data-[popup-open]:bg-surface-2 data-[popup-open]:text-fg pointer-coarse:h-11';
const popup = 'os-dark min-w-56 rounded-2xl bg-chrome p-1.5 text-fg shadow-float';

/** The studio's hand (P5g), in the pill's text colour so it reads on the light and the dark pill. */
const BrandMark = () => <Logo size={26} decorative className="text-fg" />;

/** Where the brand mark leads (P16, backlog §19): the dashboard (all boards for a local board). */
export type MainMenuHome = {
  to: string;
  label: string;
  /** True when leaving needs a confirmation (unsent edits). */ confirm?: () => boolean;
};

const markLink =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full os-press hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus pointer-coarse:h-11 pointer-coarse:w-11';

/**
 * The brand mark: a link to `home`, asking first when edits are not all sent; a plain mark without `home` (a guest by
 * link has no dashboard). The chevron next to it opens the menu.
 */
function HomeMark({ home }: { home?: MainMenuHome }) {
  const [asking, setAsking] = useState(false);
  if (!home)
    return (
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center pointer-coarse:h-11 pointer-coarse:w-11">
        <BrandMark />
      </span>
    );
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!home.confirm?.()) return;
    e.preventDefault();
    setAsking(true);
  };
  return (
    <>
      <Link
        to={home.to}
        aria-label={home.label}
        title={home.label}
        onClick={onClick}
        className={markLink}
      >
        <BrandMark />
      </Link>
      {asking && (
        <LeaveBoard
          onStay={() => setAsking(false)}
          onLeave={() => {
            setAsking(false);
            navigate(home.to);
          }}
        />
      )}
    </>
  );
}

/**
 * The board's main menu (P5d, backlog §10.1): the mark leads to the dashboard (P16) and the chevron opens a dark menu, "Actions…" first (the ⌘K
 * palette), then the model's submenus. `entries` is called each time the menu opens, so it always reads live state.
 * Items that open something run once the menu has closed, so the dialog keeps the focus.
 */
export function MainMenu({
  entries,
  onActions,
  home,
}: {
  entries: () => MainMenuEntry[];
  onActions: () => void;
  home?: MainMenuHome;
}) {
  const phone = useLayout() === 'phone';
  return (
    <span className="inline-flex shrink-0 items-center">
      <HomeMark home={home} />
      {phone ? (
        <PhoneMenu entries={entries} onActions={onActions} />
      ) : (
        <DropdownMenu entries={entries} onActions={onActions} />
      )}
    </span>
  );
}

/** Phones (P5f, backlog §14.4): the same trigger opens a full-screen sheet; the model is read when it opens. */
function PhoneMenu({
  entries,
  onActions,
}: {
  entries: () => MainMenuEntry[];
  onActions: () => void;
}) {
  const { t } = useTranslation();
  const [list, setList] = useState<MainMenuEntry[] | null>(null);
  return (
    <>
      <IconButton
        label={t('mainMenu.open')}
        icon={<Icon icon={ChevronDown} size={14} />}
        aria-haspopup="dialog"
        aria-expanded={!!list}
        onClick={() => setList(entries())}
        className={trigger}
      />
      {list && <MenuSheet entries={list} onActions={onActions} onClose={() => setList(null)} />}
    </>
  );
}

function DropdownMenu({
  entries,
  onActions,
}: {
  entries: () => MainMenuEntry[];
  onActions: () => void;
}) {
  const { t } = useTranslation();
  const after = useRef<(() => void) | null>(null);
  // Set by an item until the next opening: the closing menu must not hand the focus back over what it opened (the
  // focus is restored after `onOpenChangeComplete` has already run the item).
  const handingOff = useRef(false);
  const later = (run: () => void) => () => {
    after.current = run;
    handingOff.current = true;
  };
  // Every popup, submenus included: a submenu hands the focus back to its trigger otherwise.
  const keepFocus = () => !handingOff.current;
  return (
    <Menu.Root
      onOpenChange={(open) => {
        if (open) {
          after.current = null;
          handingOff.current = false;
        }
      }}
      onOpenChangeComplete={(open) => {
        const run = after.current;
        after.current = null;
        if (!open) run?.();
      }}
    >
      <Menu.Trigger aria-label={t('mainMenu.open')} className={trigger}>
        <Icon icon={ChevronDown} size={14} />
      </Menu.Trigger>
      <Menu.Popup label={t('mainMenu.open')} className={popup} finalFocus={keepFocus}>
        <Menu.Item
          onClick={later(onActions)}
          className="mb-1 h-9 gap-2 rounded-full border border-border bg-surface-2 text-fg-muted data-[highlighted]:border-border-strong data-[highlighted]:text-fg pointer-coarse:h-11"
        >
          <Icon icon={Search} size={16} />
          <span className="grow">{t('mainMenu.actions')}</span>
          <Kbd className="shrink-0">{formatCombo('mod+k')}</Kbd>
        </Menu.Item>
        <Menu.Separator />
        <Live entries={entries} later={later} keepFocus={keepFocus} />
      </Menu.Popup>
    </Menu.Root>
  );
}

type Later = (run: () => void) => () => void;

/** Mounted with the popup: the model is read at each opening (a render-time read would be memoized). */
function Live({
  entries,
  later,
  keepFocus,
}: {
  entries: () => MainMenuEntry[];
  later: Later;
  keepFocus: () => boolean;
}) {
  return <Entries list={entries()} later={later} keepFocus={keepFocus} />;
}

function Entries({
  list,
  later,
  keepFocus,
}: {
  list: MainMenuEntry[];
  later: Later;
  keepFocus: () => boolean;
}) {
  return list.map((entry, i) => {
    if ('separator' in entry) return <Menu.Separator key={`sep-${i}`} />;
    if ('children' in entry)
      return (
        <Menu.SubmenuRoot key={entry.id}>
          <Menu.SubmenuTrigger>
            <span>{entry.label}</span>
            <Icon icon={ChevronRight} size={14} />
          </Menu.SubmenuTrigger>
          <Menu.Popup
            label={entry.label}
            side="right"
            align="start"
            sideOffset={6}
            className={popup}
            finalFocus={keepFocus}
          >
            <Entries list={entry.children} later={later} keepFocus={keepFocus} />
          </Menu.Popup>
        </Menu.SubmenuRoot>
      );
    return (
      <Menu.Item
        key={entry.id}
        disabled={entry.disabled}
        onClick={later(entry.run)}
        className="justify-between gap-6"
        {...(entry.checked === undefined ? {} : { role: 'menuitemcheckbox', 'aria-checked': entry.checked })}
      >
        <span className="flex items-center gap-2">
          {entry.checked !== undefined && <Icon icon={Check} size={14} className={entry.checked ? '' : 'invisible'} />}
          {entry.label}
        </span>
        {entry.shortcut && <Kbd className="shrink-0">{formatCombo(entry.shortcut)}</Kbd>}
      </Menu.Item>
    );
  });
}
